// client/src/hooks/useTemplateRecibo.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const BASE_ROUTE = 'templates/recibo';
const RECIBO_TEMPLATES_QUERY_KEY = 'templatesRecibo';

export const useTemplatesRecibo = (params = {}, options = {}) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [RECIBO_TEMPLATES_QUERY_KEY, params],
        queryFn: async () => {
            try {
                const { data } = await apiClient.get(`/${BASE_ROUTE}`, { params });
                return data.data || [];
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar templates de recibo.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        staleTime: 1000 * 60 * 15,
        ...options,
    });
};

// Variáveis reconhecidas por populateReceiptTemplate (server/utils/receiptUtils.js)
// — buscar do backend em vez de manter uma lista fixa aqui evita o form
// sugerir uma variável que o backend não substitui de verdade.
export const useReciboPlaceholders = () => {
    return useQuery({
        queryKey: [RECIBO_TEMPLATES_QUERY_KEY, 'placeholders'],
        queryFn: async () => {
            const { data } = await apiClient.get(`/${BASE_ROUTE}/placeholders`);
            return data.data || [];
        },
        staleTime: 1000 * 60 * 30,
    });
};

export const useTemplateReciboById = (id, options = {}) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [RECIBO_TEMPLATES_QUERY_KEY, id],
        queryFn: async () => {
            if (!id) return null;
            try {
                const { data } = await apiClient.get(`/${BASE_ROUTE}/${id}`);
                return data.data || data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar detalhes do template de recibo.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 15,
        ...options
    });
};

export const useMutateTemplateRecibo = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    const onMutationSuccess = (message) => {
        setAlert(message, 'success');
        queryClient.invalidateQueries({ queryKey: [RECIBO_TEMPLATES_QUERY_KEY] });
    };

    const addTemplateRecibo = useMutation({
        mutationFn: (templateData) => apiClient.post(`/${BASE_ROUTE}`, templateData),
        onSuccess: () => onMutationSuccess('Template de recibo criado com sucesso!'),
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao criar template.', 'danger'),
    });

    const updateTemplateRecibo = useMutation({
        mutationFn: ({ id, templateData }) => apiClient.put(`/${BASE_ROUTE}/${id}`, templateData),
        onSuccess: () => onMutationSuccess('Template de recibo atualizado com sucesso!'),
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao atualizar template.', 'danger'),
    });

    const deleteTemplateRecibo = useMutation({
        mutationFn: (id) => apiClient.delete(`/${BASE_ROUTE}/${id}`),
        onSuccess: () => onMutationSuccess('Template de recibo removido com sucesso!'),
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao deletar template.', 'danger'),
    });

    const importTemplateRecibo = useMutation({
        mutationFn: (file) => {
            const formData = new FormData();
            formData.append("file", file);
            return apiClient.post(`/${BASE_ROUTE}/import`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
        },
        onSuccess: () => onMutationSuccess('Template de recibo importado com sucesso!'),
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao importar template.', 'danger'),
    });

    const exportTemplateRecibo = async (id) => {
        try {
            const response = await apiClient.get(`/${BASE_ROUTE}/${id}/export`, { responseType: "blob" });
            const blob = new Blob([response.data], { type: "application/json" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `recibo-template-${id}.json`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setAlert('Template exportado com sucesso!', 'success');
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Erro ao exportar template.';
            setAlert(errorMsg, 'danger');
        }
    };

    return { 
        addTemplateRecibo, 
        updateTemplateRecibo, 
        deleteTemplateRecibo,
        importTemplateRecibo,
        exportTemplateRecibo,
        isPendingRecibo: addTemplateRecibo.isPending || updateTemplateRecibo.isPending || deleteTemplateRecibo.isPending || importTemplateRecibo.isPending
    };
};